export function isCorrectQuizOption(
	option: any,
	index: number,
	correctAnswer: string | boolean | null | undefined
): boolean {

	if (!correctAnswer || typeof correctAnswer !== 'string') {
		return false;
	}

	const optionText = (option?.text || '').trim();
	const answerText = correctAnswer.trim();

	const ANSWER_PREFIX =
		'(?:correct\\s*answer|answer|ans\\.?|option|opt\\.?|choice)';

	function normalizeText(text: string): string {
		return (text || '')
			.toString()
			.replace(new RegExp(`^${ANSWER_PREFIX}[\\s:._=-]*`, 'i'), '')
			.replace(/^[A-D][.):]\s+(?=\S)/, '')
			.replace(/^\([A-D]\)\s+(?=\S)/, '')
			.replace(/^([1-9])[.)=:]\s+(?=\S)/, '')
			.replace(/^\(([1-9])\)\s+(?=\S)/, '')
			// latex cleanup
			.replace(/\$/g, '')
			.replace(/\\,/g, '')
			.replace(/\\;/g, '')
			.replace(/\\:/g, '')
			.replace(/\\!/g, '')
			.replace(/\\left/g, '')
			.replace(/\\right/g, '')
			.replace(/\\to/g, '→')
			.replace(/[{}]/g, '')
			.replace(/\s+/g, ' ')
			.trim()
			.toLowerCase();
	}

	const optionLabels = ['A', 'B', 'C', 'D'];

	if (normalizeText(optionText) === normalizeText(answerText)) {
		return true;
	}

	const prefixMatch = answerText.match(
		new RegExp(`^${ANSWER_PREFIX}[\\s:._=-]*\\(?([A-D]|[1-4])\\)?\\s*$`, 'i')
	);
	if (prefixMatch) {
		const id = prefixMatch[1].toUpperCase();
		if (/^[A-D]$/.test(id)) {
			return index === optionLabels.indexOf(id);
		}
		return index === Number(id) - 1;
	}

	const bareLetter = answerText.match(/^[\[(]?([A-D])[\])\s.:=_-]*$/i);
	if (bareLetter && answerText.length <= 3) {
		return index === optionLabels.indexOf(bareLetter[1].toUpperCase());
	}

	const letterWithContent = answerText.match(/^[\[(]?([A-D])[.):]\s+\S/);
	if (letterWithContent) {
		const letterIndex = optionLabels.indexOf(letterWithContent[1].toUpperCase());
		if (index === letterIndex) return true;
		const afterLetter = answerText.replace(/^[\[(]?[A-D][.):]\s+/, '').trim();
		if (afterLetter && normalizeText(optionText) === normalizeText(afterLetter)) {
			return true;
		}
		return false;
	}

	const numericWithContent = answerText.match(/^([1-4])[.)=:]\s+\S/);
	if (numericWithContent) {
		return index === Number(numericWithContent[1]) - 1;
	}

	return false;
}

export function getCorrectOptionIndex(
	options: any[] | null | undefined,
	correctAnswer: string | boolean | null | undefined
): number {

	if (!Array.isArray(options) || !options.length || !correctAnswer) {
		return -1;
	}

	const ANSWER_PREFIX =
		'(?:correct\\s*answer|answer|ans\\.?|option|opt\\.?|choice)';

	const optionLabels = ['A', 'B', 'C', 'D'];

	const normalizeText = (text: string): string => {
		return (text || '')
			.toString()
			.replace(new RegExp(`^${ANSWER_PREFIX}[\\s:._=-]*`, 'i'), '')
			.replace(/^[A-D][.):]\s+(?=\S)/i, '')
			.replace(/^\([A-D]\)\s+(?=\S)/i, '')
			.replace(/^([1-9])[.)=:]\s+(?=\S)/, '')
			.replace(/^\(([1-9])\)\s+(?=\S)/, '')
			.replace(/\$/g, '')
			.replace(/\\,/g, '')
			.replace(/\\;/g, '')
			.replace(/\\:/g, '')
			.replace(/\\!/g, '')
			.replace(/\\left/g, '')
			.replace(/\\right/g, '')
			.replace(/\\to/g, '→')
			.replace(/[{}]/g, '')
			.replace(/\s+/g, ' ')
			.trim()
			.toLowerCase();
	};

	const answerText = String(correctAnswer).trim();
	const normalizedAnswer = normalizeText(answerText);

	const exactMatch = options.findIndex(option =>
		normalizeText(option?.text || '') === normalizedAnswer
	);

	if (exactMatch !== -1) {
		return exactMatch;
	}

	const prefixMatch = answerText.match(
		new RegExp(
			`^${ANSWER_PREFIX}[\\s:._=-]*\\(?([A-D]|[1-9])\\)?\\s*$`,
			'i'
		)
	);

	if (prefixMatch) {
		const id = prefixMatch[1].toUpperCase();

		if (/^[A-D]$/.test(id)) {
			return optionLabels.indexOf(id);
		}

		return Number(id) - 1;
	}

	const bareLetter = answerText.match(
		/^[\[(]?([A-D])[\])\s.:=_-]*$/i
	);

	if (bareLetter && answerText.length <= 3) {
		return optionLabels.indexOf(
			bareLetter[1].toUpperCase()
		);
	}

	const letterWithContent = answerText.match(
		/^[\[(]?([A-D])[.):]\s+\S/i
	);

	if (letterWithContent) {
		const afterLetter = answerText
			.replace(/^[\[(]?[A-D][.):]\s+/i, '')
			.trim();

		const contentMatch = options.findIndex(option =>
			normalizeText(option?.text || '') === normalizeText(afterLetter)
		);

		if (contentMatch !== -1) {
			return contentMatch;
		}

		return optionLabels.indexOf(
			letterWithContent[1].toUpperCase()
		);
	}

	const numericWithContent = answerText.match(
		/^([1-9])[.)=:]\s+\S/
	);

	if (numericWithContent) {
		return Number(numericWithContent[1]) - 1;
	}

	const bareNumber = answerText.match(/^[1-9]$/);

	if (bareNumber) {
		return Number(bareNumber[0]) - 1;
	}

	return -1;
}